"use client";

import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    twttr?: {
      widgets?: {
        load: (element?: HTMLElement | null) => void;
      };
    };
  }
}

const TWITTER_WIDGET_SCRIPT = "https://platform.twitter.com/widgets.js";
let twitterWidgetScriptPromise: Promise<void> | null = null;

function loadTwitterWidgets() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.twttr?.widgets) return Promise.resolve();
  if (twitterWidgetScriptPromise) return twitterWidgetScriptPromise;

  twitterWidgetScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TWITTER_WIDGET_SCRIPT}"]`);
    if (existing) {
      const timeout = window.setTimeout(() => reject(new Error("Twitter widgets script was present but unavailable.")), 5000);
      window.setTimeout(() => {
        if (window.twttr?.widgets) {
          window.clearTimeout(timeout);
          resolve();
        }
      }, 0);
      existing.addEventListener("load", () => {
        window.clearTimeout(timeout);
        resolve();
      }, { once: true });
      existing.addEventListener("error", () => {
        window.clearTimeout(timeout);
        reject(new Error("Twitter widgets script failed to load."));
      }, { once: true });
      return;
    }

    const script = document.createElement("script");
    const timeout = window.setTimeout(() => reject(new Error("Twitter widgets script timed out.")), 8000);
    script.async = true;
    script.src = TWITTER_WIDGET_SCRIPT;
    script.charset = "utf-8";
    script.addEventListener("load", () => {
      window.clearTimeout(timeout);
      script.dataset.twitterWidgetsLoaded = "true";
      resolve();
    }, { once: true });
    script.addEventListener("error", () => {
      window.clearTimeout(timeout);
      reject(new Error("Twitter widgets script failed to load."));
    }, { once: true });
    document.body.appendChild(script);
  });

  return twitterWidgetScriptPromise;
}

export function TwitterEmbed({
  url,
  fallbackText,
  className,
}: {
  url: string;
  fallbackText?: string;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);
  const text = fallbackText?.trim() || "X/Twitter poszt";

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    loadTwitterWidgets()
      .then(() => {
        if (cancelled) return;
        window.twttr?.widgets?.load(containerRef.current);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <div ref={containerRef} className={className}>
      <blockquote className={failed ? "hidden" : "twitter-tweet"} data-dnt="true">
        <p>{text}</p>
        <a href={url} rel="noopener noreferrer">
          {url}
        </a>
      </blockquote>
      {failed ? (
        <a
          href={url}
          rel="noopener noreferrer"
          className="block rounded-lg border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-[#A4243B] hover:underline"
        >
          {text}
          <span className="mt-1 block break-all text-xs font-medium text-zinc-500">{url}</span>
        </a>
      ) : null}
    </div>
  );
}
